import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaRegSmile } from 'react-icons/fa';

const COLLAPSED_COUNT = 4;

/** Distance between the trigger and the panel. */
const GAP = 6;

/** Keep-out margin from the window edges so the strip is never half-cut. */
const VIEWPORT_MARGIN = 8;

/**
 * Reaction trigger + its picker panel, as one anchored unit.
 *
 * One definition for every pane: the direct-message and group panes each had
 * their own copy of this markup (four in total), which is why the panel drifted
 * to the left of the bubble instead of hanging off the trigger — the panel was a
 * sibling of the trigger with hard-coded `left`/`right` offsets.
 *
 * The panel is PORTALLED to <body> and fixed-positioned against the trigger's
 * viewport rect. In-flow absolute positioning could not work here: `.pane-stream`
 * is a scroll container, so it clips its own overflow — the strip was cut in half
 * at the pane edges and slid away from the smiley as the stream scrolled. Being
 * fixed, it also has to be re-measured on scroll/resize, and it flips above the
 * trigger when the window bottom is closer than the panel is tall.
 *
 * The panel opens collapsed at four emojis plus a "+" that reveals the rest, so
 * the common case is a single short strip rather than a five-row grid.
 *
 * @param align which edge of the trigger the panel prefers to line up with; use
 *   'right' for incoming bubbles (trigger on the bubble's right, panel opens
 *   inwards) and 'left' for own bubbles. Clamping can override it near an edge.
 */
const ReactionPicker = ({
  emojis = [],
  reactions = [],
  currentUserId,
  isOpen = false,
  onToggle,
  onClose,
  onSelect,
  align = 'left',
  className = '',
}) => {
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [position, setPosition] = useState(null);

  // Reopening always starts from the short strip — never from wherever the
  // previous message's picker was left.
  useEffect(() => {
    if (!isOpen) {
      setExpanded(false);
      setPosition(null);
    }
  }, [isOpen]);

  const place = useCallback(() => {
    const trigger = anchorRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;

    const rect = trigger.getBoundingClientRect();
    const { offsetWidth: width, offsetHeight: height } = panel;

    // Preferred edge first, then clamp: a strip that would hang past the window
    // slides back in rather than being clipped.
    let left = align === 'right' ? rect.right - width : rect.left;
    const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
    left = Math.min(Math.max(left, VIEWPORT_MARGIN), maxLeft);

    let top = rect.bottom + GAP;
    if (top + height + VIEWPORT_MARGIN > window.innerHeight) {
      const above = rect.top - GAP - height;
      top =
        above >= VIEWPORT_MARGIN
          ? above
          : Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);
    }

    setPosition({ top, left });
  }, [align]);

  // Before paint, so the panel never shows at its unplaced coordinates. Runs
  // again on expand, which changes the panel's measured size.
  useLayoutEffect(() => {
    if (isOpen) place();
  }, [isOpen, expanded, place]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocumentPointerDown = (event) => {
      // The panel lives in a portal, so it is NOT inside the anchor — both have
      // to be checked or mousedown would close it before the emoji's click.
      if (anchorRef.current?.contains(event.target) || panelRef.current?.contains(event.target))
        return;
      onClose?.();
    };
    const onEscape = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    // Scroll fires far faster than we can usefully re-measure; coalesce to one
    // read+write per frame so a fast scroll does not thrash layout.
    let frame = 0;
    const onViewportChange = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const rect = anchorRef.current?.getBoundingClientRect();
        // Trigger scrolled out of the window: a fixed panel would be left
        // floating over unrelated chrome.
        if (!rect || rect.bottom < 0 || rect.top > window.innerHeight) {
          onClose?.();
          return;
        }
        place();
      });
    };

    document.addEventListener('mousedown', onDocumentPointerDown);
    document.addEventListener('keydown', onEscape);
    // Capture phase: the message stream scrolls, not the window.
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', onDocumentPointerDown);
      document.removeEventListener('keydown', onEscape);
      window.removeEventListener('scroll', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange);
    };
  }, [isOpen, onClose, place]);

  const visibleEmojis = expanded ? emojis : emojis.slice(0, COLLAPSED_COUNT);
  const hasMore = emojis.length > COLLAPSED_COUNT;

  const panel = (
    <div
      ref={panelRef}
      className={`floating-menu reaction-picker is-floating ${expanded ? 'is-expanded' : ''}`}
      role="menu"
      style={{
        top: position ? position.top : 0,
        left: position ? position.left : 0,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      {visibleEmojis.length ? (
        visibleEmojis.map((emoji) => {
          const hasReacted = reactions?.some(
            (reaction) =>
              reaction?.emoji_id === emoji && String(reaction?.user_id) === String(currentUserId)
          );
          return (
            <button
              key={emoji}
              type="button"
              role="menuitem"
              className={`reaction-option ${hasReacted ? 'is-active' : ''}`}
              onClick={() => onSelect?.(emoji)}
            >
              {emoji}
            </button>
          );
        })
      ) : (
        <span className="reaction-empty">No emoji</span>
      )}

      {!expanded && hasMore && (
        <button
          type="button"
          aria-label="More reactions"
          className="reaction-option reaction-more"
          onClick={() => setExpanded(true)}
        >
          +
        </button>
      )}
    </div>
  );

  return (
    <span className={`reaction-anchor ${className}`} ref={anchorRef}>
      {/* No `title`: the native tooltip rendered on top of the open strip. */}
      <button
        type="button"
        aria-label="React"
        aria-expanded={isOpen}
        className="reaction-trigger"
        onClick={onToggle}
      >
        <FaRegSmile size={13} />
      </button>

      {isOpen && createPortal(panel, document.body)}
    </span>
  );
};

export default ReactionPicker;
